import { z } from "zod";
import type {
    CreateSatelliteContactSiteRequest,
    SatelliteContactSite,
    UpdateSatelliteContactSiteRequest,
} from "@shopexperts/legacy-api";

export interface SatelliteSiteFormValues {
    name: string;
    website_url: string;
    access_key: string;
    is_active: boolean;
    use_custom_access_key: boolean;
}

export const satelliteSiteFormSchema = z
    .object({
        name: z
            .string()
            .trim()
            .min(1, "Site name is required")
            .max(120, "Keep the name under 120 characters"),
        website_url: z
            .string()
            .trim()
            .min(1, "Website URL is required")
            .url("Enter a valid URL, including https://"),
        access_key: z.string().trim(),
        is_active: z.boolean(),
        use_custom_access_key: z.boolean(),
    })
    .superRefine((values, ctx) => {
        if (!values.use_custom_access_key) return;
        if (values.access_key.length < 8) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["access_key"],
                message: "Custom access keys must be at least 8 characters",
            });
        }
    });

export function defaultSatelliteSiteFormValues(): SatelliteSiteFormValues {
    return {
        name: "",
        website_url: "",
        access_key: "",
        is_active: true,
        use_custom_access_key: false,
    };
}

export function siteToFormValues(site: SatelliteContactSite): SatelliteSiteFormValues {
    return {
        name: site.name,
        website_url: site.website_url,
        access_key: "",
        is_active: site.is_active,
        use_custom_access_key: false,
    };
}

export function formValuesToCreatePayload(values: SatelliteSiteFormValues): CreateSatelliteContactSiteRequest {
    const payload: CreateSatelliteContactSiteRequest = {
        name: values.name.trim(),
        website_url: values.website_url.trim(),
        is_active: values.is_active,
    };

    if (values.use_custom_access_key && values.access_key.trim()) {
        payload.access_key = values.access_key.trim();
    }

    return payload;
}

export function formValuesToUpdatePayload(values: SatelliteSiteFormValues): UpdateSatelliteContactSiteRequest {
    const payload: UpdateSatelliteContactSiteRequest = {
        name: values.name.trim(),
        website_url: values.website_url.trim(),
        is_active: values.is_active,
    };

    if (values.use_custom_access_key && values.access_key.trim()) {
        payload.access_key = values.access_key.trim();
    }

    return payload;
}

export function buildDeleteSiteCopy(site: SatelliteContactSite | null) {
    const name = site?.name?.trim() || "this satellite site";
    return {
        title: "Delete satellite site?",
        text: `Removing ${name} revokes its access key immediately. Contact submissions from that website will stop working.`,
        primaryActionLabel: "Delete site",
        secondaryActionLabel: "Keep site",
    };
}

export function buildRegenerateKeyCopy(site: SatelliteContactSite | null) {
    const name = site?.name?.trim() || "this site";
    return {
        title: "Regenerate access key?",
        text: `The current key for ${name} will stop working right away. Update the satellite site with the new key after regeneration.`,
        primaryActionLabel: "Regenerate key",
        secondaryActionLabel: "Cancel",
    };
}
